import clsx from 'clsx';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { ConfirmVerifyIdentity } from '@/modules/workforce/workersTimesheet/components/Modals/VerifyWorkerImage/ConfirmVerifyIdentity';
import { IWorkersTimesheet } from '@/modules/workforce/workersTimesheet/types';
import { Button, NextImage, Typography } from '@/shared/components/atoms';
import { useModal } from '@/shared/hooks/context/modalContext/useModal';

import Building from '~/images/shared/building-3.svg';
import CameraIcon from '~/images/shared/camera.svg';
import Check from '~/images/shared/check-right.svg';
import DoorIn from '~/images/shared/door-in.svg';
import DoorOut from '~/images/shared/door-out.svg';
import Personalcard from '~/images/shared/personalcard.svg';
import VerifyManually from '~/images/shared/verify2.svg';
import Worker from '~/images/shared/Worker.svg';
import fraudIcon from '~/images/shared/xicon.svg';

interface IProfileCard {
  cardType: 'profile';
  imageSrc: string;
  attendanceRow: IWorkersTimesheet.IAttendanceTimesheetItem;
  title: string;

  isVerified?: undefined;
  takenBy?: undefined;
  date?: undefined;
  action?: undefined;
  verifiedBy?: undefined;
  fraudBy?: undefined;
  hasPP?: undefined;
}
interface ICheckCard {
  cardType: 'checkin' | 'checkout';
  imageSrc: string;
  takenBy: string;
  isVerified: 0 | 1;
  attendanceRow: IWorkersTimesheet.IAttendanceTimesheetItem;
  date: string;
  title: string;
  action: 'show' | 'review';
  verifiedBy: string;
  fraudBy: string;
  hasPP: boolean;
}
type IProps = IProfileCard | ICheckCard;
export function IdentityCard({
  cardType,
  imageSrc,
  isVerified,
  takenBy,
  attendanceRow,
  date,
  title,
  action,
  verifiedBy,
  fraudBy,
  hasPP,
}: IProps) {
  const { t } = useTranslation('common');
  const { openModal } = useModal();

  const containerClasses = clsx(
    'flex w-245 flex-col  gap-8 rounded-xl bg-white p-16'
  );
  const titleClasses = clsx(
    'flex items-center gap-5',
    isVerified === 0 && ' text-error-400'
  );
  const iconClasses = clsx('text-20', isVerified === 0 && 'stroke-error-400');

  const handleOpenConfirm = React.useCallback(
    (attendanceActionType: IWorkersTimesheet.IAttendanceActionType) => {
      if (date)
        openModal(
          <ConfirmVerifyIdentity
            attendanceActionType={attendanceActionType}
            date={date}
            workerId={attendanceRow?.workerId}
            verifyPP={!hasPP}
          />
        );
    },
    [attendanceRow?.workerId, date, openModal, hasPP]
  );

  return (
    <div className={containerClasses}>
      {/* ******** title  ********** */}
      <div className={titleClasses}>
        {cardType !== 'profile' && (
          <div className={iconClasses}>
            {cardType === 'checkin' ? <DoorIn /> : <DoorOut />}
          </div>
        )}
        <Typography variant='caption'>{title}</Typography>
      </div>
      {/* ******** image ********* */}
      <div className='h-216 overflow-hidden rounded-xl'>
        {imageSrc ? (
          <NextImage
            useSkeleton
            className='h-full w-full'
            fill
            src={imageSrc}
            alt={cardType}
          />
        ) : (
          <div className='flex h-full items-center justify-center bg-background-100 text-60'>
            <Worker />
          </div>
        )}
      </div>
      {/* **** fooooooooooooooter  **** */}
      {/* ********** profile **** */}
      {cardType === 'profile' ? (
        <div className='flex flex-col gap-8'>
          <FooterRow
            Icon={Worker}
            label={t('worker-name')}
            title={attendanceRow?.workerName}
          />

          <FooterRow
            Icon={Personalcard}
            label={t('worker-national-id')}
            title={attendanceRow?.nationalIdNumber}
          />
          <FooterRow
            Icon={Building}
            label={t('company-code')}
            title={attendanceRow?.customId}
          />
        </div>
      ) : // ********* checkin & checkout *********
      !imageSrc && !takenBy ? (
        <Typography variant='caption' className='text-surface-10'>
          {t('not-recorded-yet')}
        </Typography>
      ) : (
        <div>
          {/* ************ footer action info  ************ */}
          {takenBy && (
            <FooterRow
              Icon={CameraIcon}
              label={t('taken-by')}
              title={takenBy}
            />
          )}
          {verifiedBy && action === 'show' && (
            <FooterRow
              Icon={VerifyManually}
              label={t('verify-manually-by')}
              title={verifiedBy}
            />
          )}
          {fraudBy && action === 'show' && (
            <FooterRow
              Icon={fraudIcon}
              label={t('fraud-manually-by')}
              title={fraudBy}
            />
          )}

          {/* *********** ***** ACTIONS ******************** */}
          {action === 'review' && isVerified !== 1 && (
            <div>
              <Button
                color='red'
                variant='text'
                size='full'
                className='mb-8 mt-16 border-xs '
                onClick={() =>
                  handleOpenConfirm(
                    cardType === 'checkin'
                      ? 'fraud-worker-attendance'
                      : 'fraud-worker-dismissal'
                  )
                }
              >
                <div className=' flex items-center justify-center gap-8 whitespace-nowrap py-3'>
                  {t('consider-fraud-btn')}
                </div>
              </Button>
              <Button
                size='full'
                onClick={() =>
                  handleOpenConfirm(
                    cardType === 'checkin'
                      ? 'verify-worker-attendance'
                      : 'verify-worker-dismissal'
                  )
                }
              >
                <div className='flex items-center justify-center gap-8 whitespace-nowrap  py-3'>
                  <Check /> {t('verify-identity-btn')}
                </div>
              </Button>
            </div>
          )}
        </div>
      )}
      {!imageSrc && hasPP && action === 'review' && (
        <Button
          size='full'
          onClick={() =>
            handleOpenConfirm(
              cardType === 'checkin'
                ? 'verify-worker-attendance'
                : 'verify-worker-dismissal'
            )
          }
        >
          <div className='flex items-center justify-center gap-8 whitespace-nowrap  py-3'>
            <Check /> {t('verify-identity-btn')}
          </div>
        </Button>
      )}
    </div>
  );
}

interface IFooterRowProps {
  label: string;
  title?: string | number | null;
  Icon: React.FC;
}
const FooterRow = ({ Icon, label, title }: IFooterRowProps) => {
  return (
    <div className='flex gap-8'>
      <div className='text-24'>
        <Icon />
      </div>
      <div>
        <Typography variant='caption' className='text-surface-10'>
          {label}:
        </Typography>
        <Typography variant='subtitle1' weight='bold'>
          {title || '-'}
        </Typography>
      </div>
    </div>
  );
};
